import { v4 } from 'uuid'
import { action, observable, makeObservable, computed } from 'mobx'
import { task } from 'mobx-task'
import ModalViewModel from 'infra/ModalViewModel'
import JobStore, {
  AdminJobScopeUpdateCustomItemToCreateParams,
  AdminJobScopeUpdateCustomItemToRemoveParams,
  AdminJobScopeUpdateInventoryItemParams,
} from 'jobs/JobStore'
import { required, validationContext } from 'validx'
import InventoryStore from 'admin/InventoryStore'
import { Id } from 'types/definitions'
import Job, { JobScopeItem } from 'jobs/Job'
import InventoryItem from 'admin/inventory/InventoryItem'
import ManageItemsViewModel from './ManageItemsViewModel/ManageItemsViewModel'
import { maxLength } from 'utils/validation-utils'

export interface CustomItemsToRemove {
  id: string
}

/**
 * View Model for updating the job scope as an admin.
 */
export default class UpdateJobScopeViewModel extends ModalViewModel {
  manageItemsVM = new ManageItemsViewModel(
    this.inventoryStore,
    this.job,
    this.workspaceId,
    this.addInventoryItem,
    this.setSelectedInventoryItems,
    this.setSelectedCustomItems,
    this.getAllCurrentItemsForManageItemsVM.bind(this),
    this.addCustomItemToBeRemoved,
  )

  readonly maxReasonLength = 280

  @observable
  state = UpdateJobScopeState.UpdateJobScope

  @observable
  error = ''

  @observable
  reason = ''

  @observable
  selectedInventoryItems: JobScopeInventoryItem[] = []

  @observable
  selectedCustomItems: JobScopeCustomItem[] = []

  @observable
  selectedCustomItemIdsToRemove: CustomItemsToRemove[] = []

  constructor(
    private job: Job,
    private inventoryStore: InventoryStore,
    private workspaceId: Id,
    private jobStore: JobStore,
  ) {
    super()
    makeObservable(this)

    this.show$.subscribe(() => this.getJobScopeItemsFromJob())
    this.close$.subscribe(this.reset)
  }

  validation = validationContext(this, {
    reason: [
      maxLength(
        this.maxReasonLength,
        `The reason can't be longer than ${this.maxReasonLength} characters.`,
      ),
    ],
  })

  /**
   * Update the job scope.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const customItemsToAdd: AdminJobScopeUpdateCustomItemToCreateParams[] =
      this.selectedCustomItems
        .filter((item) => item.source === JobScopeItemSource.Added)
        .map((item) => ({
          name: item.name,
          quantity: item.newQuantity,
          price: item.price!,
          proPrice: item.proPrice!,
        }))

    const customItemsToRemove: AdminJobScopeUpdateCustomItemToRemoveParams[] =
      this.selectedCustomItemIdsToRemove.map((item) => ({
        custom_item_id: item.id,
      }))

    // Only change the job scope for the inventory items that have a different new quantity
    const inventoryItems: AdminJobScopeUpdateInventoryItemParams[] =
      this.selectedInventoryItems
        .filter((item) => item.newQuantity !== item.currentQuantity)
        .map((item) => ({
          versionId: item.versionId,
          quantity: item.newQuantity,
        }))

    try {
      if (this.job.isDraft) {
        await this.jobStore.adminDraftJobScopeUpdate(
          this.job,
          this.reason,
          customItemsToAdd,
          customItemsToRemove,
          inventoryItems,
        )
        this.setState(UpdateJobScopeState.Success)
        return
      }

      await this.jobStore.adminJobScopeUpdate(
        this.job,
        this.reason,
        customItemsToAdd,
        customItemsToRemove,
        inventoryItems,
      )
      this.setState(UpdateJobScopeState.Success)
    } catch (err) {
      console.error('Job scope update failed.', err)
      this.error = err.message
      this.setState(UpdateJobScopeState.Failed)
    }
  })

  @computed
  get isNoServiceItemsOnJob() {
    return (
      this.selectedCustomItems.length === 0 &&
      this.selectedInventoryItems.filter((i) => i.canShow).length === 0
    )
  }

  @computed
  get disabled() {
    const customItemsIsNotValid =
      this.selectedCustomItems.filter((item) => item.newQuantity < 0).length > 0
    const inventoryItemsIsNotValid =
      this.selectedInventoryItems.filter((item) => item.newQuantity < 0)
        .length > 0

    if (customItemsIsNotValid || inventoryItemsIsNotValid) {
      return true
    }

    const containsAddedCustomItems =
      this.selectedCustomItems.filter(
        (item) => item.source === JobScopeItemSource.Added,
      ).length > 0

    const isCustomItemQuantityChanged =
      this.selectedCustomItems.filter(
        (i) => i.source === JobScopeItemSource.Job,
      ).length !==
      this.job.jobScopeItems.filter((i) => i.custom_item_id !== null).length

    const isJobScopeInventoryItemsEqualToSelectedInventoryItems =
      this.isEqualItemsFromJobScope(
        this.job.jobScopeItems.filter((i) => i.inventory_item_version_id),
        this.selectedInventoryItems,
      )

    return containsAddedCustomItems || isCustomItemQuantityChanged
      ? false
      : isJobScopeInventoryItemsEqualToSelectedInventoryItems
  }

  /**
   * Reason text box characters.
   */
  @computed
  get reasonCharsRemaining() {
    if (this.reason) {
      return this.maxReasonLength - this.reason.length
    }

    return this.maxReasonLength
  }

  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Resets the state.
   */
  @action.bound
  reset() {
    this.submit.reset()
    this.setState(UpdateJobScopeState.UpdateJobScope)

    this.reason = ''
    this.error = ''
    this.selectedInventoryItems = []
    this.selectedCustomItems = []
    this.selectedCustomItemIdsToRemove = []
    this.validation.reset()
  }

  @action.bound
  setState(state: UpdateJobScopeState) {
    this.state = state
  }

  @action.bound
  setSelectedCustomItems(items: JobScopeCustomItem[]) {
    this.selectedCustomItems = items
  }

  @action.bound
  addCustomItemToBeRemoved(id: string) {
    this.selectedCustomItemIdsToRemove.push({ id })
  }

  @action.bound
  addCustomItem(customItem: JobScopeCustomItem) {
    this.selectedCustomItems.push(customItem)
  }

  @action.bound
  addInventoryItem(inventoryItem: JobScopeInventoryItem) {
    this.selectedInventoryItems.push(inventoryItem)
  }

  @action.bound
  setSelectedInventoryItems(inventoryItems: JobScopeInventoryItem[]) {
    this.selectedInventoryItems = inventoryItems
  }

  @action.bound
  removeCustomItem(id: string) {
    const customItem = this.selectedCustomItems.find((item) => item.id === id)
    if (customItem?.source === JobScopeItemSource.Job) {
      this.addCustomItemToBeRemoved(id)
    }

    this.selectedCustomItems = this.selectedCustomItems.filter(
      (item) => item.id !== id,
    )
    this.manageItemsVM.setSelectedCustomItems(this.selectedCustomItems)
  }

  getJobScopeItemsFromJob() {
    this.job.jobScopeItems.forEach((item) => {
      // We don't want to show the bundle in this list.
      if (item.bundle_id !== null) {
        return
      }

      if (item.custom_item_id !== null) {
        const customItem = new JobScopeCustomItem(JobScopeItemSource.Job)
        customItem.setId(item.custom_item_id)
        customItem.setName(item.name)
        customItem.setCurrentQuantity(item.quantity)
        customItem.setPrice(item.price ?? 0)
        customItem.setProPrice(item.pro_price ?? 0)
        this.addCustomItem(customItem)
        return
      }

      const inventoryItem = new JobScopeInventoryItem(item, (i) =>
        i.removeFromJobScope(),
      )

      this.addInventoryItem(inventoryItem)
    })

    this.manageItemsVM.setSelectedInventoryItems(
      this.selectedInventoryItems.slice(),
    )
    this.manageItemsVM.setSelectedCustomItems(this.selectedCustomItems.slice())
  }

  getAllCurrentItemsForManageItemsVM() {
    this.manageItemsVM.setSelectedInventoryItems(
      this.selectedInventoryItems.slice(),
    )
    this.setSelectedCustomItems(
      this.selectedCustomItems.filter((item) => item.name !== ''),
    )
    this.manageItemsVM.setSelectedCustomItems(this.selectedCustomItems.slice())
  }

  isEqualItemsFromJobScope = (
    jobScopeInventoryItems: JobScopeItem[],
    selectedInventoryItems: JobScopeInventoryItem[],
  ): boolean => {
    if (jobScopeInventoryItems.length !== selectedInventoryItems.length) {
      return false
    }

    return jobScopeInventoryItems.every((item1) => {
      const foundItem = selectedInventoryItems.find(
        (i) => i.versionId === item1.inventory_item_version_id,
      )
      if (!foundItem) {
        return false
      }

      return (
        foundItem.id === item1.inventory_item_id &&
        foundItem.versionId === item1.inventory_item_version_id &&
        foundItem.newQuantity === item1.quantity
      )
    })
  }
}

/**
 * Describes the state of the view model.
 */
export enum UpdateJobScopeState {
  /**
   * Admin can update the current job scope or add more items.
   */
  UpdateJobScope,
  /**
   * Admin can specify the reason for the job scope update.
   */
  SpecifyReason,
  /**
   * Collaborators on the job will be notified of the changes.
   */
  Success,
  /**
   * Something went wrong while executing the update job scope command, could be a
   * validation error or something transient.
   */
  Failed,
}

/**
 * The source of a job scope item.
 */
export enum JobScopeItemSource {
  Unknown,
  Job,
  Added,
}

export class JobScopeCustomItem {
  @observable id = ''

  @observable name = ''

  @observable newQuantity = 1

  @observable currentQuantity = 0

  @observable price = 0

  @observable proPrice = 0

  @observable source = JobScopeItemSource.Unknown

  readonly validation = validationContext(this, {
    name: [required('Service name is required.')],
  })

  validate() {
    this.validation.reset()

    return this.validation.validate().isValid
  }

  constructor(source: JobScopeItemSource) {
    makeObservable(this)

    this.source = source
    this.id = this.source === JobScopeItemSource.Added ? v4() : ''
    this.newQuantity =
      this.source === JobScopeItemSource.Job ? this.currentQuantity : 1
  }

  @action.bound
  incrementQuantity() {
    this.newQuantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.newQuantity === 0) {
      this.newQuantity = 0
    } else {
      this.newQuantity -= 1
    }
  }

  @action.bound
  setId(value: string) {
    this.id = value
  }

  @action.bound
  setCurrentQuantity(value: number) {
    this.currentQuantity = value

    if (this.source === JobScopeItemSource.Job) {
      this.newQuantity = value
    }
  }

  @action.bound
  setQuantity(value: number) {
    this.newQuantity = value
  }

  @action.bound
  setName(value: string) {
    this.name = value
  }

  @action.bound
  setPrice(value: number) {
    this.price = value
  }

  @action.bound
  setProPrice(value: number) {
    this.proPrice = value
  }
}

export class JobScopeInventoryItem {
  @observable id = ''

  @observable versionId = ''

  @observable name = ''

  @observable description = ''

  @observable selected = false

  @observable newQuantity = 1

  @observable currentQuantity = 0

  @observable price = 0

  @observable proPrice = 0

  @observable bundleId: string | null

  @observable canShow = true

  readonly validation = validationContext(this, {
    newQuantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
  })

  validate() {
    this.validation.reset()

    return this.validation.validate().isValid
  }

  constructor(
    item: InventoryItem | JobScopeItem,
    private onSelectedChange: (item: JobScopeInventoryItem) => void,
  ) {
    makeObservable(this)

    this.id = item instanceof InventoryItem ? item.id : item.inventory_item_id!
    this.versionId =
      item instanceof InventoryItem
        ? item.versionId
        : item.inventory_item_version_id!
    this.name = item.name
    this.description = item.description
    this.currentQuantity = item instanceof InventoryItem ? 0 : item.quantity
    this.newQuantity = item instanceof InventoryItem ? 1 : item.quantity
    this.selected = true
    this.price = item.price ?? 0
    this.proPrice =
      (item instanceof InventoryItem ? item.proPrice : item.pro_price) ?? 0
    this.bundleId = item instanceof InventoryItem ? null : item.bundle_id
  }

  @action.bound
  incrementQuantity() {
    this.newQuantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.newQuantity <= 0) {
      this.newQuantity = 0
    } else {
      this.newQuantity -= 1
    }
  }

  @action.bound
  setQuantity(value: string) {
    this.newQuantity = parseInt(value, 10) || 1
  }

  @action.bound
  toggle() {
    this.selected = !this.selected
    this.onSelectedChange(this)
  }

  /**
   * This is used to hide the item when "removed" from the list
   * and sets the newQuantity to 0 to remove it from the Job Scope.
   */
  @action.bound
  removeFromJobScope() {
    this.canShow = false
    this.newQuantity = 0
  }
}
